import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Chip from "@material-ui/core/Chip";
import Autocomplete from "@material-ui/lab/Autocomplete";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const NotificationRuleSchema = Yup.object().shape({
  name: Yup.string().min(2, "Muito curto").required("Obrigatório"),
});

const keywordsToText = keywords => (keywords || []).join("\n");
const textToKeywords = text =>
  (text || "")
    .split("\n")
    .map(k => k.trim())
    .filter(k => k.length > 0);

const NotificationRuleModal = ({ open, onClose, ruleId }) => {
  const classes = useStyles();

  const initialState = {
    name: "",
    active: true,
    keywordsText: "",
  };

  const [rule, setRule] = useState(initialState);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/list");
        setUsers(data.map(u => ({ id: u.id, name: u.name })));
      } catch (err) {
        toastError(err);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchRule = async () => {
      if (!ruleId) {
        setRule(initialState);
        setSelectedUsers([]);
        return;
      }
      try {
        const { data } = await api.get(`/notification-rules/${ruleId}`);
        setRule({
          name: data.name,
          active: data.active,
          keywordsText: keywordsToText(data.keywords),
        });
        setSelectedUsers(
          (data.users || []).map(u => ({ id: u.id, name: u.name }))
        );
      } catch (err) {
        toastError(err);
      }
    };
    fetchRule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleId, open]);

  const handleClose = () => {
    onClose();
    setRule(initialState);
    setSelectedUsers([]);
  };

  const handleSaveRule = async values => {
    const finalKeywords = textToKeywords(values.keywordsText);

    if (finalKeywords.length === 0) {
      toast.error("Adicione ao menos uma palavra ou frase.");
      return;
    }

    const { keywordsText, ...rest } = values;
    const ruleData = {
      ...rest,
      keywords: finalKeywords,
      userIds: selectedUsers.map(u => u.id),
    };

    try {
      if (ruleId) {
        await api.put(`/notification-rules/${ruleId}`, ruleData);
      } else {
        await api.post("/notification-rules", ruleData);
      }
      toast.success("Regra de notificação salva com sucesso!");
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {ruleId ? "Editar regra de notificação" : "Nova regra de notificação"}
        </DialogTitle>
        <Formik
          initialValues={rule}
          enableReinitialize={true}
          validationSchema={NotificationRuleSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveRule(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label="Nome da regra"
                      name="name"
                      autoFocus
                      placeholder="Ex: Imposto de Renda"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label="Palavras ou frases"
                      name="keywordsText"
                      placeholder={"Uma por linha, pode colar direto. Ex:\nimposto de renda\ndeclaração\nIRPF"}
                      helperText="Uma palavra/frase por linha. Pode digitar ou colar (Ctrl+V) normalmente."
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      multiline
                      minRows={3}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={users}
                      value={selectedUsers}
                      onChange={(e, value) => setSelectedUsers(value)}
                      getOptionLabel={option => option.name}
                      getOptionSelected={(option, value) => option.id === value.id}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option.name}
                            size="small"
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderInput={params => (
                        <TextField
                          {...params}
                          variant="outlined"
                          margin="dense"
                          label="Quem deve ser notificado"
                          placeholder="Selecione os atendentes"
                          helperText="Só recebem o aviso os usuários com WhatsApp de aviso cadastrado (Usuários → editar)"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.active}
                          onChange={e => setFieldValue("active", e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Regra ativa"
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {ruleId ? "Salvar" : "Adicionar"}
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default NotificationRuleModal;
